/**
 * Word-like editor for briefs and scripts (docs/RICH-TEXT.md, Aziz 2026-10-02): bold,
 * italic, underline, size, text colour, the yellow highlighter, lists, and a direction per
 * paragraph. Each paragraph reads its own direction from its first letter (Arabic right
 * to left, English left to right) unless the toolbar forces one, and a line mixing both
 * keeps every word in reading order (unicode-bidi: plaintext).
 */
import { useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Color, FontSize, TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { Bold, Eraser, Highlighter, Italic, List, ListOrdered, Underline as UnderlineIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { RICH_CLS, plainToHtml } from './RichView';

const BLOCKS = ['paragraph', 'heading', 'listItem', 'blockquote'];

/** A direction on every block: auto (default), rtl or ltr. */
const Direction = Extension.create({
  name: 'direction',
  addGlobalAttributes() {
    return [{
      types: BLOCKS,
      attributes: {
        dir: {
          default: 'auto',
          parseHTML: (el: HTMLElement) => (['rtl', 'ltr', 'auto'].includes(el.getAttribute('dir') ?? '') ? el.getAttribute('dir') : 'auto'),
          renderHTML: (a: { dir?: string }) => ({ dir: a.dir || 'auto' }),
        },
      },
    }];
  },
});

const SIZES = [
  { label: 'Small', value: '13px' },
  { label: 'Normal', value: '' },
  { label: 'Large', value: '18px' },
  { label: 'Larger', value: '22px' },
];
const COLOURS = [
  { label: 'Default', value: '' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Green', value: '#22c55e' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Purple', value: '#a855f7' },
];
const YELLOW = '#fde047';

function Btn({ on, title, onClick, children, testid }: { on?: boolean; title: string; onClick: () => void; children: React.ReactNode; testid?: string }) {
  return (
    <button type="button" title={title} aria-label={title} aria-pressed={!!on} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      data-testid={testid}
      className={cn('h-7 min-w-7 px-1.5 rounded inline-flex items-center justify-center text-xs', on ? 'bg-accent-600/30 text-accent-200' : 'text-muted hover:text-muted-strong hover:bg-white/5')}>
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const st = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'), italic: e.isActive('italic'), underline: e.isActive('underline'),
      yellow: e.isActive('highlight'), bullets: e.isActive('bulletList'), numbers: e.isActive('orderedList'),
      size: (e.getAttributes('textStyle').fontSize as string) ?? '', colour: (e.getAttributes('textStyle').color as string) ?? '',
      dir: (e.getAttributes('paragraph').dir as string) || (e.getAttributes('heading').dir as string) || 'auto',
    }),
  });
  const dir = (d: string) => {
    let ch = editor.chain().focus();
    for (const t of BLOCKS) ch = ch.updateAttributes(t, { dir: d });
    ch.run();
  };
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-800 px-1.5 py-1" data-testid="rich-toolbar">
      <Btn on={st.bold} title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} testid="rich-bold"><Bold size={14} /></Btn>
      <Btn on={st.italic} title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14} /></Btn>
      <Btn on={st.underline} title="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()} testid="rich-underline"><UnderlineIcon size={14} /></Btn>
      <span className="w-px h-5 bg-slate-800 mx-1" />
      <select value={st.size} onChange={(e) => (e.target.value ? editor.chain().focus().setFontSize(e.target.value).run() : editor.chain().focus().unsetFontSize().run())}
        title="Size" aria-label="Size" data-testid="rich-size" className="h-7 rounded bg-bg border border-slate-800 text-xs text-muted-strong px-1">
        {SIZES.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
      </select>
      <span className="inline-flex items-center gap-0.5 ms-1" role="group" aria-label="Text colour">
        {COLOURS.map((c) => (
          <button key={c.label} type="button" title={`Text colour: ${c.label}`} aria-label={`Text colour: ${c.label}`} onMouseDown={(e) => e.preventDefault()}
            onClick={() => (c.value ? editor.chain().focus().setColor(c.value).run() : editor.chain().focus().unsetColor().run())}
            data-testid={`rich-colour-${c.label.toLowerCase()}`}
            className={cn('h-5 w-5 rounded-full border', st.colour === c.value || (!c.value && !st.colour) ? 'ring-2 ring-accent-400 border-transparent' : 'border-slate-600')}
            style={{ background: c.value || 'transparent' }} />
        ))}
      </span>
      <Btn on={st.yellow} title="Yellow highlighter" onClick={() => editor.chain().focus().toggleHighlight({ color: YELLOW }).run()} testid="rich-highlight">
        <Highlighter size={14} className={st.yellow ? 'text-yellow-300' : ''} />
      </Btn>
      <span className="w-px h-5 bg-slate-800 mx-1" />
      <Btn on={st.bullets} title="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={14} /></Btn>
      <Btn on={st.numbers} title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={14} /></Btn>
      <span className="w-px h-5 bg-slate-800 mx-1" />
      <Btn on={st.dir === 'rtl'} title="Right to left (Arabic)" onClick={() => dir('rtl')} testid="rich-rtl"><span className="font-semibold">ع</span>&#x2190;</Btn>
      <Btn on={st.dir === 'ltr'} title="Left to right (English)" onClick={() => dir('ltr')} testid="rich-ltr">&#x2192;<span className="font-semibold">A</span></Btn>
      <Btn on={st.dir === 'auto'} title="Direction from the first letter" onClick={() => dir('auto')} testid="rich-auto"><span className="text-[10px]">Auto</span></Btn>
      <span className="w-px h-5 bg-slate-800 mx-1" />
      <Btn title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().run()}><Eraser size={14} /></Btn>
    </div>
  );
}

export default function RichEditor({ html, plain, onChange, placeholder, disabled, minHeight = 140, testid }: {
  /** the formatted text, when there is one */
  html?: string | null;
  /** the plain text, used when there is no formatted copy yet */
  plain?: string | null;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
  testid?: string;
}) {
  // Tiptap re-applies its options whenever they are new objects, which snapped the cursor
  // back on every keystroke (a click did not move it, first letters were lost): every
  // option is built once and kept, and the latest onChange is read through a ref.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const extensions = useMemo(() => [
    StarterKit.configure({ link: false, code: false, codeBlock: false, horizontalRule: false, heading: { levels: [2, 3] } }),
    TextStyle, Color, FontSize, Highlight.configure({ multicolor: true }), Direction,
  ], []);
  const editorProps = useMemo(() => ({ attributes: { 'data-placeholder': placeholder ?? '', dir: 'auto' } }), [placeholder]);
  const initial = useRef(html || plainToHtml(plain ?? ''));
  const editor = useEditor({
    extensions,
    content: initial.current,
    editable: !disabled,
    onUpdate: ({ editor: e }) => onChangeRef.current(e.isEmpty ? '' : e.getHTML()),
    editorProps,
  });
  useEffect(() => { editor?.setEditable(!disabled); }, [editor, disabled]);
  // a new value from outside (another record loaded) replaces the content
  useEffect(() => {
    if (!editor) return;
    const want = html || plainToHtml(plain ?? '');
    if (want !== editor.getHTML() && !editor.isFocused) editor.commands.setContent(want, { emitUpdate: false });
  }, [html, plain]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!editor) return null;
  return (
    <div className={cn('rounded-lg border border-slate-800 bg-bg focus-within:border-accent-400', disabled && 'opacity-60')} data-testid={testid}
      // Inside a <label>, a click anywhere is passed on to the label's first control, which
      // here is the Bold button: every click into the text pressed Bold (2026-10-02). The
      // editor never relies on the click's default action, so it is stopped.
      onClick={(e) => { if ((e.currentTarget as HTMLElement).closest('label')) e.preventDefault(); }}>
      {!disabled && <Toolbar editor={editor} />}
      <EditorContent editor={editor}
        className={cn(RICH_CLS, 'px-3 py-2 text-sm text-muted-strong',
          '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[var(--rich-min)]',
          '[&_.ProseMirror.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted/50')}
        style={{ ['--rich-min' as string]: `${minHeight}px` }} />
    </div>
  );
}
